import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, UserX } from "lucide-react";
import { api, errMsg, ASSET_STATUS, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge, DataTable, DirectoryPicker, Field, PageHeader, Select } from "@/components/common";
import Modal from "@/components/Modal";

const EMPTY = { type: "", brand: "", model: "", serial_number: "", inventory_number: "", supplier_id: "", invoice_id: "", purchase_date: "", price_ht: 0, warranty_end: "", assigned_name: "", assigned_email: "", service: "", location: "", status: "en_stock", cost_center: "" };

export default function Assets() {
  const { user, settings } = useAuth();
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [sups, setSups] = useState([]);
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const editable = can(user, "createur");
  const load = () => api.get("/assets").then((r) => { setRows(r.data); const a = r.data.find((x) => x.id === params.get("id")); if (a) setForm(a); });
  useEffect(() => { load(); api.get("/suppliers").then((r) => setSups(r.data)); }, []); // eslint-disable-line
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async (e) => {
    e.preventDefault();
    if (!form.type.trim() || !form.cost_center) return toast.error("Le type et le centre de coût sont obligatoires");
    if (Number(form.price_ht) < 0) return toast.error("Le prix doit être positif");
    try {
      const body = { ...form, price_ht: Number(form.price_ht) };
      form.id ? await api.put(`/assets/${form.id}`, body) : await api.post("/assets", body);
      toast.success("Fiche matériel enregistrée");
      setForm(null);
      load();
    } catch (x) { toast.error(errMsg(x)); }
  };
  const types = [...new Set(rows.map((r) => r.type))].sort();
  const columns = [
    { key: "inventory_number", label: "N° inventaire", render: (r) => <span className="mono text-xs font-semibold">{r.inventory_number}</span> },
    { key: "type", label: "Type" },
    { key: "brand", label: "Marque" },
    { key: "model", label: "Modèle" },
    { key: "serial_number", label: "N° série", render: (r) => <span className="mono text-xs">{r.serial_number}</span> },
    { key: "supplier", label: "Fournisseur", value: (r) => sups.find((s) => s.id === r.supplier_id)?.name || "" },
    { key: "purchase_date", label: "Achat", type: "date" },
    { key: "price_ht", label: "Prix HT", type: "money" },
    { key: "warranty_end", label: "Fin garantie", type: "date" },
    { key: "assigned_name", label: "Affecté à", render: (r) => <span>{r.assigned_name || r.service}{r.assigned_inactive && <span className="ml-1 inline-flex items-center gap-1 rounded bg-[#B03A3A] px-1.5 text-[10px] font-bold text-white"><UserX size={10} /> Compte désactivé</span>}</span> },
    { key: "location", label: "Localisation" },
    { key: "cost_center", label: "Centre de coût" },
    { key: "status", label: "Statut", value: (r) => ASSET_STATUS[r.status][0], render: (r) => <Badge meta={ASSET_STATUS[r.status]} /> },
  ];
  const inp = (k, label, type = "text") => <Field label={label}><input type={type} className="inp" value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} disabled={!editable} data-testid={`asset-${k}-input`} /></Field>;
  return (
    <div>
      <PageHeader title="Inventaire du matériel" subtitle="Alertes de fin de garantie (60 jours) et matériel affecté à des comptes Microsoft 365 désactivés.">
        {editable && <button className="btn-primary" onClick={() => setForm({ ...EMPTY })} data-testid="new-asset-btn"><Plus size={16} /> Nouveau matériel</button>}
      </PageHeader>
      <DataTable columns={columns} rows={rows.filter((r) => (!status || r.status === status) && (!type || r.type === type))} title="Matériel" testid="assets-table" onRowClick={setForm}
        filters={<><Select className="!w-40" value={status} onChange={setStatus} placeholder="Tous statuts" options={Object.entries(ASSET_STATUS).map(([value, [label]]) => ({ value, label }))} testid="assets-status-filter" />
          <Select className="!w-44" value={type} onChange={setType} placeholder="Tous types" options={types.map((t) => ({ value: t, label: t }))} testid="assets-type-filter" /></>} />
      <Modal open={!!form} onClose={() => setForm(null)} title={form?.inventory_number || "Nouveau matériel"} wide testid="asset-modal">
        {form && (
          <form onSubmit={save} className="grid gap-4 sm:grid-cols-3">
            {inp("type", "Type *")}{inp("brand", "Marque")}{inp("model", "Modèle")}
            {inp("serial_number", "N° de série")}{inp("inventory_number", "N° d'inventaire (auto si vide)")}
            <Field label="Fournisseur"><Select value={form.supplier_id} onChange={(v) => set("supplier_id", v)} placeholder="—" options={sups.map((s) => ({ value: s.id, label: s.name }))} testid="asset-supplier-select" /></Field>
            {inp("purchase_date", "Date d'achat", "date")}{inp("price_ht", "Prix HT (€)", "number")}{inp("warranty_end", "Fin de garantie", "date")}
            <Field label="Utilisateur affecté (annuaire)"><DirectoryPicker value={form.assigned_name} onSelect={(p) => setForm({ ...form, assigned_name: p.name, assigned_email: p.email, service: p.department, location: form.location || p.office, status: "affecte" })} testid="asset-user-picker" /></Field>
            {inp("service", "Service")}{inp("location", "Localisation")}
            <Field label="Centre de coût *"><Select value={form.cost_center} onChange={(v) => set("cost_center", v)} placeholder="— Choisir —" options={(settings?.my_cost_centers || []).map((c) => ({ value: c.code, label: `${c.code} – ${c.label}` }))} testid="asset-cost-center-select" /></Field>
            <Field label="Statut"><Select value={form.status} onChange={(v) => set("status", v)} options={Object.entries(ASSET_STATUS).map(([value, [label]]) => ({ value, label }))} testid="asset-status-select" /></Field>
            {form.invoice_number && <p className="self-end text-xs text-[#6B6B80]">Facture d'achat : {form.invoice_number}</p>}
            {editable && <div className="flex justify-end gap-2 sm:col-span-3">
              {form.id && <button type="button" className="btn-ghost" onClick={async () => { await api.delete(`/assets/${form.id}`); setForm(null); load(); }}>Archiver (réformé)</button>}
              <button className="btn-primary" data-testid="asset-save-btn">Enregistrer</button>
            </div>}
          </form>
        )}
      </Modal>
    </div>
  );
}
